import Typography from "antd/lib/typography";
import { Dispatch, SetStateAction, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { IUserResource } from "@sol/sdk";
import { IClassroomUser } from "@sol/sdk/classrooms/types/IClassroom";
import { getResourceUuid } from "@sol/sdk/utils/getResourceUuid";
import { getUserDisplayName } from "@sol/sdk/utils/getUserDisplayName";
import { Empty } from "@sol/uikit/data-display/Empty/Empty";
import { List } from "@sol/uikit/data-display/List/List";
import type { ListItemBase, ListSelectionMode } from "@sol/uikit/data-display/List/List.types";
import { ListItem } from "@sol/uikit/data-display/List/ListItem";

import { UserAvatar } from "./UserAvatar";

type UserListItem = ListItemBase & {
    user: IClassroomUser | IUserResource;
};

type Props = {
    users?: (IClassroomUser | IUserResource)[];
    setSelectedUser: Dispatch<SetStateAction<IClassroomUser | IUserResource | undefined>>;
    selectedUserId?: string | string[];
    isLoading?: boolean;
    allowMultipleSelection?: boolean;
    showUserAvatar?: boolean;
    className?: string;
};

export const UsersList = ({
    className,
    users = [],
    setSelectedUser,
    selectedUserId,
    allowMultipleSelection = false,
    isLoading = false,
    showUserAvatar = true,
}: Props) => {
    const [t] = useTranslation();

    const items = useMemo<UserListItem[]>(
        () =>
            users.map(user => ({
                key: getResourceUuid(user),
                user,
                disabled: false,
            })),
        [users],
    );

    const selectedKeys = useMemo<string[]>(() => {
        if (!selectedUserId) {
            return [];
        }
        return Array.isArray(selectedUserId) ? selectedUserId : [selectedUserId];
    }, [selectedUserId]);

    const handleSelectionChange = (keys: string[]) => {
        const selectedKey = allowMultipleSelection ? keys[keys.length - 1] : keys[0];
        if (!selectedKey) {
            return;
        }

        const selectedUser = users.find(user => getResourceUuid(user) === selectedKey);

        if (selectedUser) {
            setSelectedUser(selectedUser);
        }
    };
    const selectionMode: ListSelectionMode = allowMultipleSelection ? "multiple" : "single";

    return (
        <List
            rootClassName="w-full"
            items={items}
            selectionMode={selectionMode}
            selectedKeys={selectedKeys}
            onSelectionChange={handleSelectionChange}
            showSelectionIndicator={false}
            loading={isLoading}
            emptyState={<Empty description={t("component.UsersList.noResult")} />}
            className={className}
            data-cy="users-list"
            renderItem={({ item, props }) => {
                const { user } = item;
                const displayName = getUserDisplayName(user);

                return (
                    <ListItem
                        {...props}
                        className="flex h-auto w-full items-center gap-ant-xs p-ant-xs transition-all"
                        data-cy="users-list-item"
                    >
                        {showUserAvatar && <UserAvatar className="shrink-0" size="default" user={user} />}
                        <span className="flex flex-1 flex-col overflow-hidden text-ellipsis">
                            <Typography.Text
                                className="mb-0! w-full flex-1 overflow-hidden text-left text-ant-h5! leading-ant-h5! font-ant-strong break-all"
                                title={displayName}
                                ellipsis
                            >
                                {displayName}
                            </Typography.Text>
                            <Typography.Text
                                className="text-ant-md -mt-ant-xxs w-full flex-1 overflow-hidden text-left font-normal break-all text-ellipsis text-color-ant-secondary"
                                title={user.email}
                                ellipsis
                            >
                                {user.email}
                            </Typography.Text>
                        </span>
                    </ListItem>
                );
            }}
        />
    );
};
